import { useEffect, useRef, useState, type RefObject } from 'react';
import { initialFolderVideoQuality, prepareFolderVideo, folderVideoRetryDelay, FolderVideoAccessError, type FolderVideoQuality } from '../api/folderVideoStream';

/** Android Explorer transport adapted for public folders and native Safari HLS. */
export function useFolderVideo(videoRef: RefObject<HTMLVideoElement | null>, original: string) {
  const [quality, setQuality] = useState<FolderVideoQuality>(initialFolderVideoQuality);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState('Открываем видео…');
  const [error, setError] = useState('');
  const position = useRef(0), wantPlay = useRef(true);
  useEffect(() => {
    const video = videoRef.current; if (!video) return;
    const controller = new AbortController(); let cleanup = () => {}, retryTimer = 0, stallTimer = 0, retries = 0, loading = true, canResume = false, generation = 0;
    let preparing: AbortController | null = null;
    const updatePosition = () => { if (video.readyState > 0 && Number.isFinite(video.currentTime)) position.current = video.currentTime; };
    const stop = () => { loading = true; cleanup(); cleanup = () => {}; };
    const retry = (message = 'Восстанавливаем соединение…') => {
      if (controller.signal.aborted || retryTimer) return;
      updatePosition(); stop(); setStatus(navigator.onLine ? message : 'Ждём подключения к интернету…');
      if (!navigator.onLine) return;
      if (++retries > 12) { setStatus(''); setError('Не удаётся продолжить просмотр. Проверьте подключение и повторите.'); return; }
      retryTimer = window.setTimeout(() => { retryTimer = 0; void connect(); }, folderVideoRetryDelay(retries));
    };
    const play = () => {
      if (wantPlay.current) void video.play().catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        if (reason instanceof DOMException && reason.name === 'NotAllowedError') { setStatus(''); wantPlay.current = false; }
      });
    };
    const connect = async () => {
      if (controller.signal.aborted) return;
      const current = ++generation;
      preparing?.abort(); preparing = new AbortController();
      stop(); setError(''); setStatus('Открываем видео…');
      try {
        {
          const url = await prepareFolderVideo(original, quality, preparing.signal, setStatus, attempt > 0 || retries > 0);
          const { default: Hls } = await import('hls.js/light');
          if (controller.signal.aborted || current !== generation) return;
          const native = Boolean(video.canPlayType('application/vnd.apple.mpegurl'));
          if (native && (/Apple/.test(navigator.vendor) || !Hls.isSupported())) {
            video.src = url;
            cleanup = () => { video.pause(); video.removeAttribute('src'); video.load(); };
            loading = false; canResume = true; video.load(); play(); return;
          }
          if (!Hls.isSupported()) throw new FolderVideoAccessError('Браузер не поддерживает потоковый просмотр.');
          const policy = { maxTimeToFirstByteMs: 15000, maxLoadTimeMs: 30000, timeoutRetry: { maxNumRetry: 2, retryDelayMs: 1000, maxRetryDelayMs: 4000 }, errorRetry: { maxNumRetry: 3, retryDelayMs: 1000, maxRetryDelayMs: 8000 } };
          const hls = new Hls({
            autoStartLoad: false, startPosition: position.current, lowLatencyMode: false,
            maxBufferLength: 24, maxMaxBufferLength: 30, maxBufferSize: 8 * 1024 * 1024, backBufferLength: 6,
            liveDurationInfinity: false, maxLiveSyncPlaybackRate: 1,
            manifestLoadPolicy: { default: policy }, playlistLoadPolicy: { default: policy }, fragLoadPolicy: { default: policy },

          });
          let mediaRecoveries = 0;
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (!data.fatal || controller.signal.aborted) return;
            if ([401,403,410].includes(data.response?.code || 0)) { stop(); setStatus(''); setError('Доступ к видео закончился. Откройте его снова.'); return; }
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveries++ < 2) { hls.recoverMediaError(); return; }
            retry();
          });
          hls.on(Hls.Events.MANIFEST_PARSED, () => { if (!controller.signal.aborted) { hls.startLoad(position.current); play(); } });
          cleanup = () => { hls.destroy(); video.pause(); video.removeAttribute('src'); video.load(); };
          hls.attachMedia(video); hls.loadSource(url);
        }
        loading = false; canResume = true;

      } catch (reason) {
        if (controller.signal.aborted || current !== generation) return;
        if (reason instanceof FolderVideoAccessError) { setStatus(''); setError(reason.message); }
        else retry();
      }
    };
    const online = () => { if (!video.ended && wantPlay.current) { updatePosition(); clearTimeout(retryTimer); retryTimer = 0; retries = 0; void connect(); } };
    const waiting = () => {
      if (!wantPlay.current) return;
      setStatus(navigator.onLine ? 'Подгружаем видео…' : 'Ждём подключения к интернету…');
      clearTimeout(stallTimer);
      stallTimer = window.setTimeout(() => {
        if (!controller.signal.aborted && !video.paused && quality === '360' && canResume) { updatePosition(); setQuality('240'); }
      }, 8000);
    };
    const playing = () => { clearTimeout(stallTimer); wantPlay.current = true; setStatus(''); setError(''); retries = 0; };
    // Removing/replacing a MediaSource emits a delayed pause event. It is not a
    // user pause and must not cancel playback after reconnecting.
    const pause = () => { if (!loading && !video.seeking && video.readyState >= 2 && !video.error) { wantPlay.current = false; clearTimeout(stallTimer); setStatus(''); } };
    const metadata = () => { if (position.current) video.currentTime = Math.min(position.current, Number.isFinite(video.duration) ? Math.max(0, video.duration - .05) : position.current); play(); };
    const resume = () => { wantPlay.current = true; };
    const failed = () => { if (!loading) retry(); };
    video.addEventListener('error', failed);
    video.addEventListener('timeupdate', updatePosition); video.addEventListener('waiting', waiting); video.addEventListener('playing', playing);
    video.addEventListener('pause', pause); video.addEventListener('play', resume); video.addEventListener('loadedmetadata', metadata);
    window.addEventListener('online', online);
    void connect();
    return () => {
      updatePosition(); controller.abort(); preparing?.abort(); clearTimeout(retryTimer); clearTimeout(stallTimer);
      video.removeEventListener('error', failed);
      video.removeEventListener('timeupdate', updatePosition); video.removeEventListener('waiting', waiting); video.removeEventListener('playing', playing);
      video.removeEventListener('pause', pause); video.removeEventListener('play', resume); video.removeEventListener('loadedmetadata', metadata);
      window.removeEventListener('online', online); stop();
    };
  // A quality change reconnects at the last played position.
  }, [videoRef, original, quality, attempt]);
  return { status, error, quality, retry: () => { wantPlay.current = true; setAttempt(value => value + 1); } };
}
